import { Component, EventEmitter, Input, NgZone, OnInit, OnChanges, Output, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { GridComponent, GridDataResult, DataStateChangeEvent, RowClassArgs } from '@progress/kendo-angular-grid';
import { Router } from '@angular/router';
import { process, State } from '@progress/kendo-data-query';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ContactMultiselectDialog } from '../contact/contact-email-popup.component';
import { CSEService } from '../../services/cse.service';
import { EmailService } from '../../services/email.service';
import { EmployeeService } from '../../services/employees.service';
import { EncryptionService } from '../../services/encryption.service';
import { ERPService } from '../../services/erp.service';
import { NotificationService } from '../../services/notification.service';
import { OrderService } from '../../services/order.service';
import { RecordLockService } from '../../services/recordlock.service';
import { UtilService } from '../../services/util.service';
import {
    CarrierDropReason,
    Email,
    Employee,
    Offer,
    QuoteOrderLineTypeTags,
    SearchCarrierContact,
    SearchCarrierSalesLoads,
    SearchTagType
} from '../../models/models';

@Component({
    selector: 'carrier-sales-released-loads-grid',
    templateUrl: './carrier-sales-released-loads-grid.component.html',
    standalone: false
})
export class CarrierSalesReleasedLoadsGrid implements OnInit, OnChanges {
    @Input() loads: SearchCarrierSalesLoads[] = [];
    @Input() viewType: string = "released";
    @Input() selectedLoad: SearchCarrierSalesLoads = {} as SearchCarrierSalesLoads;
    @Output() onClick = new EventEmitter<any>();
    @Output() onRemoveCarrier = new EventEmitter<any>();
    //@Output() onOfferClick = new EventEmitter<any>();

    @ViewChild(GridComponent, { static: false }) public releasedLoadsGrid!: GridComponent;

    public isLoading: boolean = false;
    public isCSRManager: boolean = false;
    public loadStatus: string = "Released";
    public filteredLoads: SearchCarrierSalesLoads[] = [];
    public contacts: SearchCarrierContact[] = [];
    //public isCarrierRepsAssigned: boolean = false;

    // Carrier Sales Reps Assigned dialog fields
    public quoteOrderLineID: number = 0;
    public assignedLoad: SearchCarrierSalesLoads = {} as SearchCarrierSalesLoads;
    public removeCarrierOffer: Offer = {} as Offer;
    public carrierDropReasonList: CarrierDropReason[] = [];
    public quoteOrderLineTypeTagsList: QuoteOrderLineTypeTags[] = [];
    public tagTypeList: SearchTagType[] = [];
    public carrierReps: Employee[] = [];

    private recordID: number = 0;
    private recordLockAction: string = "";
    private recordLockSource: string = "Procurement";

    public gridState: State = { skip: 0, take: 10, sort: [{ dir: 'asc', field: 'arrivalStartDate' }] };
    //public gridState: State = { sort: [{ dir: 'asc', field: 'arrivalStartDate' }] };
    public gridData: GridDataResult = { data: [], total: 0 };
    //public pageSizes: number[] = [5, 10, 20, 50, 100, 200, 500];

    constructor(public cseService: CSEService, public emailService: EmailService, public employeeService: EmployeeService,
        public encryptionService: EncryptionService, public erpService: ERPService, private ngZone: NgZone,
        private notificationService: NotificationService, private orderService: OrderService,
        private recordLockService: RecordLockService, private utilService: UtilService,
        public dialog: MatDialog, private router: Router) {
    }

    ngOnInit() {
        this.orderService.getSearchTagTypes().subscribe(result => {
            this.tagTypeList = result.resultCollection;
        });
        this.isCSRManager = this.getIsCSRManager();
        this.updateLoads();
        this.getCarrierDropReasons();
        this.getQuoteOrderLineTypeTags();
        this.getCarrierReps();
        //console.log("loads", this.loads.length);
        this.initRecordLock();
    }

    ngOnChanges() {
        this.updateLoads();
    }

    private getIsCSRManager() {
        var employeeRoles = localStorage.getItem('EmployeeRoles');

        //console.log(employeeRoles);
        if (employeeRoles != null && employeeRoles.length > 0) {
            if (employeeRoles.indexOf('Carrier Sales Manager') > -1) return true;
        }

        return false;
    }

    private getCarrierDropReasons() {
        this.cseService.getAllCarrierDropReasons().subscribe(result => {
            this.carrierDropReasonList = result.resultCollection;
            //console.log("getCarrierDropReasons", this.carrierDropReasonList);
        });
    }

    private getCarrierReps() {
        this.employeeService.getEmployeesByGroupName('CarrierRep').subscribe(result => {
            var emps = result;
            emps = emps.sort((left, right): number => {
                if (left.fullName < right.fullName) return -1;
                if (left.fullName > right.fullName) return 1;
                return 0;
            });
            for (var i = 0; i < emps.length; i++) {
                emps[i].winUserName = (emps[i].winUserName == null ? "" : emps[i].winUserName.toUpperCase());
            }
            this.carrierReps = emps;
            this.fitColumns();
        });
    }

    private getQuoteOrderLineTypeTags() {
        this.orderService.getQuoteOrderLineTypeTags().subscribe(result => {
            this.quoteOrderLineTypeTagsList = result;
        });
    }

    private updateLoads() {
        this.filteredLoads = Object.assign([], this.loads);

        if (this.viewType == "orders") {
            this.loadStatus = "Orders";
            this.filteredLoads = this.filteredLoads.filter(f =>
                f.loadType == "ORD"
                && f.carrierID == 0
            );
        }
        else if (this.viewType == "booked") {
            this.loadStatus = "Booked";
        }
        else if (this.viewType == "quotes") {
            this.loadStatus = "Quotes";
            this.filteredLoads = this.filteredLoads.filter(f =>
                (f.loadType == "OQ" || f.loadType == "QO")
                && f.finalCost == 0
            );
        }
        else if (this.viewType == "rfq") {
            this.loadStatus = "RFQ";
            this.filteredLoads = this.filteredLoads.filter(f =>
                (f.loadType == "RFQ" || f.loadType == "RP")
                && f.carrierID == 0
            );
        }
        else if (this.viewType == "open-dedicated") {
            this.loadStatus = "Open Dedicated";
            this.filteredLoads = this.filteredLoads.filter(f =>
                f.loadType == "OD"
                && f.carrierID == 0
                && f.finalCost == 0
            );
        }
        this.fitColumns();
        this.gridData = process(this.filteredLoads, this.gridState);
        //console.log("updateLoads", this.filteredLoads);
    }

    private fitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.releasedLoadsGrid == null) return;
            this.releasedLoadsGrid.autoFitColumns();
        });
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        this.gridState = state;
        //this.updateLoads();
        this.gridData = process(this.loads, this.gridState);
        this.fitColumns();
    }

    //public loadClick(load: SearchCarrierSalesLoads) {
    //    //console.log("carrier-sales-loads-grid.loadClick", load);
    //    this.onOfferClick.emit(load);
    //}

    //public rowSelected(e: any) {
    //    console.log("carrier-sales-loads-grid.rowSelected", e, this.assignedLoad);
    //    if (e.selectedRows && !this.assignedLoad.quoteOrderLineID) {
    //        // Only fire if clicking the row and not a link or button in the row
    //        var load = e.selectedRows[0].dataItem;
    //        this.selectedLoad = load;
    //        //console.log("carrier-sales-loads-grid.rowSelected", e, load);

    //        this.loadClick(load);
    //    }
    //    //this.onClick.emit(load);
    //}

    public rowCallback(context: RowClassArgs) {
        const isHighPriority = (context.dataItem.loadPriorityAbbreviation == "High");
        const isExpedite = (context.dataItem.rateIndex == "Expedite");
        if (isExpedite) {
            return {
                expedite: isExpedite
            }
        }
        else if (isHighPriority) {
            return {
                highPriority: isHighPriority
            }
        }
    }

    //public editCarrierReps(load: SearchCarrierSalesLoads) {
    //    this.assignedLoad = Object.assign({}, load);
    //}

    public carrierRepChange(lineID: number, load: SearchCarrierSalesLoads, evt: any) {
        var repID = evt.target.value;
        //console.log("carrierRepChange", lineID, repID, load);
        this.isLoading = true;
        this.cseService.saveOfferFromSearchCarrierSalesBookedLoads(load).subscribe(result => {
            this.isLoading = false;
            //console.log("saveOfferFromSearchCarrierSalesLoads", result);
        });
    }

    public quoteOrderLineTypeTagsChange(abbr: string, load: SearchCarrierSalesLoads, evt: any) {
        console.log("quoteOrderLineTypeTagsChange", abbr, load, evt);
        this.isLoading = true;
        this.cseService.saveOfferFromSearchCarrierSalesBookedLoads(load).subscribe(result => {
            this.isLoading = false;
            //console.log("saveOfferFromSearchCarrierSalesLoads", result);
        });
    }

    public cancelDialogClick() {
        var emptyLoad: SearchCarrierSalesLoads = {} as SearchCarrierSalesLoads;
        var emptyOffer: Offer = {} as Offer;
        this.assignedLoad = emptyLoad;
        this.selectedLoad = emptyLoad;
        this.removeCarrierOffer = emptyOffer;
    }

    public editTender(load: SearchCarrierSalesLoads) {
        this.onClick.emit(load);
        //this.onOfferClick.emit(load.awardedOfferID);
    }

    public viewTracking(quoteOrderID: string) {
        this.encryptionService.getEncryptedValue(quoteOrderID).subscribe(result => {
            var encryptedquoteOrderID = result;
            var relativeUrl = this.router.createUrlTree(['order-tracking', encryptedquoteOrderID]);
            var baseUrl = window.location.href.replace(this.router.url, "");
            window.open(baseUrl + relativeUrl, "_blank");
            //this.router.navigate(['order-tracking', encryptedOrderNumber]);
        });
    }

    public linkMP(quoteOrderLineID: number) {
        console.log("linkMP", quoteOrderLineID);
        this.erpService.getMacropointLink(quoteOrderLineID).subscribe(result => {
            var url = result.resultCollection[0];
            if (url == "") {
                this.notificationService.showWarning("System failed to retrieve link from Macropoint. Please make sure this load is setup in their system.", "WARNING: Macropoint Link");
            }
            else {
                window.open(url, "_blank");
            }
        });
    }

    public removeCarrier(load: SearchCarrierSalesLoads) {
        this.recordID = load.quoteOrderID;
        this.selectedLoad = load;
        this.recordLockAction = "removeCarrier";
        //this.recordLockService.checkHasRecordLock("QuoteOrder", load.quoteOrderID, this.recordLockSource);
        this.handleRecordLock(this.recordID);
    }

    public allowRemoveCarrier(load: SearchCarrierSalesLoads) {
        //console.log("removeCarrier", load);
        var isStarted: boolean = load.loadStatus != "Tendered";
        if (isStarted) {
            if (confirm("All stops will be unactualized. Proceed?")) {
                this.removeCarrierLoadOffer(load);
            }
        }
        else {
            this.removeCarrierLoadOffer(load);
        }
    }

    private removeCarrierLoadOffer(load: SearchCarrierSalesLoads) {
        //console.log("removeCarrierLoadOffer", load);
        this.isLoading = true;
        this.cseService.getOfferByID(load.awardedOfferID!).subscribe(result => {
            this.isLoading = false;
            this.removeCarrierOffer = result.resultCollection[0];
        });
    }

    public removeCarrierSave() {
        console.log("removeCarrierSave", this.removeCarrierOffer);
        this.isLoading = true;
        this.removeCarrierOffer.updatedByEmployeeID = this.utilService.getEmployeeID();
        this.utilService.setAuditNotes("Released Loads: Remove Carrier");
        this.cseService.removeAwardOffer(this.removeCarrierOffer).subscribe(result => {
            this.isLoading = false;
            this.cancelDialogClick();
            this.recordLockService.clearRecordLock("QuoteOrder", this.recordID, this.recordLockSource);
            this.recordLockService.resetRecordLock();
            this.onRemoveCarrier.emit();
            console.log("removeCarrierSave:result", result);
        });
    }

    public sendEmail(load: SearchCarrierSalesLoads) {
        console.log("sendEmail", load);
        var contacts: Email[] = [];
        var selectedContacts: Email[] = [];
        var typedEmails: string = "";
        var defaultContact: Email | undefined;

        for (var i = 0; i < this.contacts.length; i++) {
            if (this.contacts[i].email != null && this.contacts[i].email != "") {
                contacts.push({
                    email: this.contacts[i].email,
                    name: this.contacts[i].contactName
                });
            }
        }

        if (load.carrierContactEmail != null && load.carrierContactEmail != "") {
            defaultContact = {
                email: load.carrierContactEmail,
                name: load.carrierContactName
            }
        }


        if (load.bookedByCarrierRepEmail != null && load.bookedByCarrierRepEmail != "") {
            typedEmails = load.bookedByCarrierRepEmail;
        }

        //console.log("dialog.open", contacts, defaultContact, selectedContacts, typedEmails);

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            showNotes: true,
            contacts: contacts,
            defaultContact: defaultContact,
            selectedContacts: selectedContacts,
            typedEmails: typedEmails
        }

        const dialogRef = this.dialog.open(ContactMultiselectDialog, dialogConfig);

        //dialogRef.componentInstance.notes = dataItem.comment;

        dialogRef.afterClosed().subscribe(result => {
            //console.log("sendEmail:dialogRef.afterClosed()", result);
            if (result.btnOK == true) {
                if (defaultContact == undefined) {
                    if (selectedContacts.length > 0) {
                        defaultContact = selectedContacts[0];
                    }
                    else {
                        defaultContact = {
                            email: "",
                            name: ""
                        }
                    }
                }

                if (result == null ||
                    (
                        (defaultContact.email == null || defaultContact.email == "")
                        && (result.selectedContacts == null || result.selectedContacts.length < 1)
                        && (result.selectedEmails == null || result.selectedEmails.length < 1)
                    )
                ) return;

                var ccEmails = "";
                if (result.selectedContacts != null) {
                    result.selectedContacts.forEach(e => {
                        ccEmails += e.email + "|";
                    });
                }
                if (result.typedEmails && result.typedEmails.indexOf(",") >= 0) {
                    var typedEmails = result.typedEmails.split(",");
                    typedEmails.forEach(e => {
                        var email = e.trim();
                        if (ccEmails.indexOf(email) < 0) {
                            ccEmails += email + "|";
                        }
                    });
                }
                else {
                    ccEmails += result.typedEmails;
                }

                var subject = "Carrier award offer for order #" + load.orderNumber;

                this.isLoading = true;
                this.emailService.sendRateConfirmationCustom(load.quoteOrderLineID, defaultContact.email, ccEmails, "", -1, subject, result.notes).subscribe(emailResult => {
                    this.isLoading = false;
                    var ok = emailResult;
                    console.log("sendEmail Result", emailResult);
                });
            //this.emailService.sendEmail(defaultContact.email, emails, "", -1, subject, result.notes).subscribe(emailResult => {
            //    this.isLoading = false;
            //    var ok = emailResult;
            //    this.isSaving = false;
            //    this.onSaveClick.emit();
            //});
            }
        });
    }

    public getTagTypes(idList: string) {
        var tagTypes: SearchTagType[] = [];
        if (idList && idList != "") {
            var arr: number[] = [];
            arr = idList.replace(/, +/g, ",").split(",").map(Number);
            for (var i = 0; i < arr.length; i++) {
                var idx: number = this.tagTypeList.findIndex(t => t.tagTypeID == arr[i]);
                if (idx >= 0) {
                    tagTypes.push(this.tagTypeList[idx]);
                }
            }
        }
        return tagTypes;
    }

    private initRecordLock() {
        this.recordLockAction = "";
        this.recordLockService.continueRecordLockObserver.subscribe(result => {
            //console.log("order-form:handleRecordLock:isContinueRecorLock", this.recordLockService.isContinueRecordLock);
            if (this.recordLockService.isContinueRecordLock) {
                //this.continueRecordLockAction();
                if (!this.recordLockService.isLocked && this.recordLockAction == "removeCarrier") {
                    this.allowRemoveCarrier(this.selectedLoad);
                }
            }
        });
        this.recordLockService.cancelOverrideObserver.subscribe(result => {
            //console.log("order-form:initRecordLock:isCancelOverride", this.recordLockService.isCancelOverride);
            if (this.recordLockService.isCancelOverride) {
                if (this.recordLockAction == "removeCarrier") {
                    this.recordLockService.isLocked = false;
                    this.cancelDialogClick();
                }
            }
        });
        this.recordLockService.timeoutRecordLockObserver.subscribe(result => {
            if (this.recordLockService.isRecordLockTimeout) {
                //console.log("order-form:handleRecordLock:isRecordLockTimeout", this.recordLockAction, this.crossBorderLoad.quoteOrderID, this.recordLockService.searchRecordLock);
                this.recordLockService.clearRecordLock("QuoteOrder", this.recordID, this.recordLockSource);
                //this.recordLockService.clearRecordLock(this.recordLockService.recordLock.recordLockID);
                //this.continueRecordLockAction();
            }
        });
        this.recordLockService.timeoutConfirmObserver.subscribe(result => {
            if (this.recordLockService.isConfirmTimeout) {
                //console.log("order-form:handleRecordLock:timeoutConfirmObserver", this.recordLockAction, this.quoteOrder.quoteOrderId, this.recordLockService.searchRecordLock);
                this.recordLockService.resetRecordLock();
                //this.navigateQuoteOrder(this.quoteOrderID, true);
                if (this.recordLockAction == "removeCarrier") {
                    this.cancelDialogClick();
                }
            }
        });
        this.recordLockService.hasRecordLockObserver.subscribe(result => {
            //console.log("order-form:handleRecordLock:isContinueRecorLock", this.recordLockService.isContinueRecordLock);
            if (this.recordLockService.hasRecordLock) {
                if (this.recordLockAction == "removeCarrier") {
                    this.allowRemoveCarrier(this.selectedLoad);
                }
                this.recordLockAction = "";
            }
        });
    }

    private handleRecordLock(id: number) {
        //console.log("order-form:handleRecordLock", id);
        this.recordLockService.handleRecordLock("QuoteOrder", id, this.recordLockSource);
    }
}
